import React, { useCallback, useEffect, useState } from "react";
import fetchApi from "../../services/FetchApi";
import * as GoogleAnalyticsService from "../../services/GoogleAnalyticsService";
import { signOut } from "../../services/SessionApi";
import { Loader } from "../common";
import Signup from "../Signup/Signup";
import ConsentScreen, {
	ClientInfo,
	Failure,
	FailureScreen,
	parseClientInfo,
} from "./ConsentScreen";
import { restoreSession, Session } from "./session";

type Phase =
	| { kind: "resolving" }
	| { kind: "login"; client: ClientInfo }
	| { kind: "consent"; session: Session; client: ClientInfo; busy: boolean }
	| { kind: "failed"; failure: Failure };

const failureOf = (err: any): Failure => {
	switch (err?.response?.data?.error) {
		case "expired_oauth_request":
			return "expired";
		case "invalid_oauth_request":
			return "invalid";
		default:
			return "unavailable";
	}
};

export type OauthFlowProps = { oauthRequest: string; challengeId: string };

// The authorization step of "Sign in with Select". Phoenix validated and
// signed the request; this page establishes who is signed in, asks for
// consent when the user hasn't already granted it, and follows the
// redirect Phoenix builds for the answer.
export default function OauthFlow({ oauthRequest, challengeId }: OauthFlowProps) {
	const [phase, setPhase] = useState<Phase>({ kind: "resolving" });

	const answer = useCallback(
		async (session: Session, approved: boolean) => {
			const { redirect_to } = await fetchApi.oauthConsent(
				oauthRequest,
				approved,
				session.jwt,
			);
			window.location.replace(redirect_to);
		},
		[oauthRequest],
	);

	const resolve = useCallback(async () => {
		setPhase({ kind: "resolving" });
		// A framed consent screen could be clickjacked into "Allow".
		if (window.top !== window.self) {
			setPhase({ kind: "failed", failure: "framed" });
			return;
		}
		try {
			const session = await restoreSession();
			const client = parseClientInfo(
				await fetchApi.oauthClientInfo(oauthRequest, session?.jwt),
			);
			if (!session) {
				setPhase({ kind: "login", client });
			} else if (client.requireConsent) {
				setPhase({ kind: "consent", session, client, busy: false });
				GoogleAnalyticsService.logEvent("oauth_consent_shown", { client_id: client.id });
			} else {
				await answer(session, true);
			}
		} catch (err) {
			setPhase({ kind: "failed", failure: failureOf(err) });
		}
	}, [oauthRequest, answer]);

	useEffect(() => {
		resolve();
	}, [resolve]);

	const decide = async (approved: boolean) => {
		if (phase.kind !== "consent") return;
		setPhase({ ...phase, busy: true });
		GoogleAnalyticsService.logEvent(
			approved ? "oauth_consent_approved" : "oauth_consent_denied",
			{ client_id: phase.client.id },
		);
		try {
			await answer(phase.session, approved);
		} catch (err) {
			setPhase({ kind: "failed", failure: failureOf(err) });
		}
	};

	const switchAccount = async () => {
		if (phase.kind !== "consent") return;
		await signOut().catch(() => undefined);
		setPhase({ kind: "login", client: phase.client });
	};

	switch (phase.kind) {
		case "resolving":
			return (
				<div className="fixed inset-0">
					<Loader />
				</div>
			);
		case "login":
			return (
				<Signup
					challengeId={challengeId}
					oauthRequest={oauthRequest}
					onAuthenticated={resolve}
					linkId={phase.client.linkId ?? undefined}
					title={`Continue to ${phase.client.name}`}
					logo={phase.client.logoUrl ?? undefined}
					coverImage={phase.client.coverImageUrl ?? undefined}
					backgroundColor={phase.client.backgroundColor ?? undefined}
				/>
			);
		case "consent":
			return (
				<ConsentScreen
					client={phase.client}
					user={phase.session.user}
					busy={phase.busy}
					onAllow={() => decide(true)}
					onDeny={() => decide(false)}
					onSwitchAccount={switchAccount}
				/>
			);
		case "failed":
			return <FailureScreen failure={phase.failure} onRetry={resolve} />;
		default: {
			const _exhaustive: never = phase;
			return _exhaustive;
		}
	}
}
